import React, {useCallback} from 'react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {AccountTreeRounded as LinkEntityIcon, TextFields as ReplaceIcon} from '@mui/icons-material';
import {getEntityTypeLabel} from '../../../../../utils/entityTypeMeta';

// Static sx objects hoisted to module scope
const PRE_SX = {
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    margin: 0,
    color: 'text.secondary',
};
const SUGGESTED_TEXT_SX = {
    p: 1,
    backgroundColor: 'rgba(0,0,0,0.04)',
    borderRadius: 1,
    fontStyle: 'italic',
};
const LINK_BTN_SX = {mt: 0.75, textTransform: 'none'};
const REPLACE_BTN_SX = {mt: 0.5, textTransform: 'none'};

/**
 * Content for entity recognition tasks
 */
function EntityRecognitionContent({task, onLinkEntity, canLinkEntity}) {
    const handleLinkClick = useCallback((e) => {
        e.stopPropagation();
        onLinkEntity?.();
    }, [onLinkEntity]);

    return (
        <>
            <Typography variant="body2">
                <strong>Entity Type:</strong> {getEntityTypeLabel(task.entityType) || task.entityType}
            </Typography>
            <Typography variant="body2">
                <strong>Entity Name:</strong> {task.entityName}
            </Typography>

            <Button
                variant="contained"
                size="small"
                startIcon={<LinkEntityIcon/>}
                onClick={handleLinkClick}
                disabled={!canLinkEntity}
                sx={LINK_BTN_SX}
            >
                Link entity
            </Button>
        </>
    );
}

/**
 * Content for compliance check tasks
 */
function ComplianceCheckContent({task, onReplaceText, isReplacing, viewAvailable}) {
    const handleReplaceClick = useCallback((e) => {
        e.stopPropagation();
        onReplaceText?.();
    }, [onReplaceText]);

    return (
        <>
            {(task.instructions && task.instructions.length > 0) ? (
                <>
                    <Typography variant="body2" fontWeight="bold">
                        Instructions:
                    </Typography>
                    <Stack component="ul" spacing={0.5} sx={{pl: 2, my: 0}}>
                        {task.instructions.map((instruction, index) => (
                            <Typography key={index} component="li" variant="body2">
                                {instruction}
                            </Typography>
                        ))}
                    </Stack>
                </>
            ) : null}
            {task.suggestedText ? (
                <>
                    <Typography variant="body2" fontWeight="bold">
                        Suggested Text:
                    </Typography>
                    <Typography variant="body2" sx={SUGGESTED_TEXT_SX}>
                        {task.suggestedText}
                    </Typography>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<ReplaceIcon/>}
                        onClick={handleReplaceClick}
                        disabled={isReplacing || !viewAvailable}
                        sx={REPLACE_BTN_SX}
                    >
                        {isReplacing ? 'Replacing...' : 'Replace text'}
                    </Button>
                </>
            ) : null}
        </>
    );
}

/**
 * Fallback content showing raw task data
 */
function FallbackContent({task}) {
    return (
        <Typography component="pre" sx={PRE_SX}>
            {JSON.stringify(task, null, 2)}
        </Typography>
    );
}

/**
 * Content component for task marker - shows task details when expanded
 */
const TaskMarkerContent = React.memo(function TaskMarkerContent({
    task,
    loading,
    notFound,
    onReplaceText,
    isReplacing,
    viewAvailable,
    onLinkEntity,
    canLinkEntity
}) {
    if (loading) {
        return (
            <Typography variant="body2" sx={{color: 'text.secondary'}}>
                Loading task details...
            </Typography>
        );
    }

    if (notFound) {
        return (
            <Typography variant="body2" sx={{color: 'warning.main'}}>
                Task data not found in Firestore. The task may have been deleted or not yet created.
            </Typography>
        );
    }

    if (!task?.taskType) {
        return (
            <Typography variant="body2" sx={{color: 'text.secondary'}}>
                Task data loaded but missing taskType property.
            </Typography>
        );
    }

    if (task.taskType === 'entity_recognition') {
        return <EntityRecognitionContent task={task} onLinkEntity={onLinkEntity}
                                         canLinkEntity={canLinkEntity && viewAvailable}/>;
    }

    if (task.taskType === 'compliance_check') {
        return (
            <ComplianceCheckContent
                task={task}
                onReplaceText={onReplaceText}
                isReplacing={isReplacing}
                viewAvailable={viewAvailable}
            />
        );
    }

    return <FallbackContent task={task}/>;
});

export default TaskMarkerContent;
